<script setup lang="ts">
import { computed, ref, shallowRef, watch } from "vue";
import { useRatingService } from "../../../cbi-generator/frontend/composables/useRatingFrontendService";
import type { PersonRatingGrid } from "../../../cbi-generator/generated/dto/PersonRatingGrid";
import type { PersonRatingGridCell } from "../../../cbi-generator/generated/dto/PersonRatingGridCell";
import { PersonRatingGridLevel } from "../../../cbi-generator/generated/dto/PersonRatingGridLevel";

const props = defineProps<{
  personId: number;
  mode: "staff" | "member";
  refreshKey?: number;
}>();

const service = useRatingService();
const grid = shallowRef<PersonRatingGrid | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const section = ref<string | null>(null);
let version = 0;

const levels: Record<PersonRatingGridLevel, { label: string; color: string }> =
  {
    [PersonRatingGridLevel.NONE]: { label: "", color: "default" },
    [PersonRatingGridLevel.GREEN]: { label: "Green", color: "success" },
    [PersonRatingGridLevel.YELLOW]: { label: "Yellow", color: "warning" },
    [PersonRatingGridLevel.RED]: { label: "Red", color: "error" },
    [PersonRatingGridLevel.LEAD]: { label: "Lead", color: "default" },
  };

const current = computed(
  () =>
    grid.value?.sections.find((s) => s.key === section.value) ??
    grid.value?.sections[0],
);

function label(cell: PersonRatingGridCell) {
  return levels[cell.level].label || (cell.rated ? "Rated" : "Not Rated");
}

async function load() {
  const requested = ++version;
  loading.value = true;
  error.value = null;
  try {
    const person = { personId: props.personId };
    const result =
      props.mode === "staff"
        ? await service.getPersonRatingGridStaff(person)
        : await service.getPersonRatingGridMember(person);
    if (requested !== version) return;
    grid.value = result;
    if (!result.sections.some((s) => s.key === section.value))
      section.value = result.sections[0]?.key ?? null;
  } catch (e) {
    if (requested !== version) return;
    error.value = e instanceof Error ? e.message : "Failed to load ratings.";
  } finally {
    if (requested === version) loading.value = false;
  }
}

watch(() => [props.personId, props.mode, props.refreshKey], load, {
  immediate: true,
});
</script>

<template>
  <div>
    <v-alert v-if="error" type="error" class="mb-4"
      >{{ error }}
      <v-btn :disabled="loading" @click="load">Retry</v-btn></v-alert
    >
    <v-tabs
      v-if="grid"
      v-model="section"
      class="mb-4"
      color="primary"
      hide-slider
      density="compact"
    >
      <v-tab
        v-for="item in grid.sections"
        :key="item.key"
        :value="item.key"
        :text="item.title"
        class="text-none me-2"
        rounded="lg"
        variant="flat"
      />
    </v-tabs>
    <v-card variant="outlined" elevation="0" border="thin" :loading="loading">
      <v-card-text v-if="current" class="pa-0">
        <v-table>
          <tbody>
            <tr v-for="row in current.rows" :key="row.title">
              <th>{{ row.title }}</th>
              <td
                v-for="(cell, index) in row.cells"
                :key="`${row.title}-${index}`"
              >
                <v-chip
                  v-if="cell.ratingId == null"
                  variant="text"
                  color="scrollbar_hover"
                  class="w-100 justify-center"
                  >&mdash;</v-chip
                >
                <v-chip
                  v-else-if="cell.rated"
                  variant="tonal"
                  class="w-100 justify-center"
                  :title="cell.ratingName ?? undefined"
                >
                  <v-icon
                    :color="levels[cell.level].color"
                    icon="mdi-shield-check-outline"
                    class="mr-1"
                  />
                  {{ label(cell) }}
                </v-chip>
                <v-chip
                  v-else
                  variant="text"
                  color="scrollbar_hover"
                  class="w-100 justify-center"
                  :title="cell.ratingName ?? undefined"
                  >{{ label(cell) }}</v-chip
                >
              </td>
              <td
                v-for="i in Math.max(current.columnCount - row.cells.length, 0)"
                :key="`${row.title}-pad-${i}`"
              ></td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>
  </div>
</template>
